import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Heart } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../contexts/CartContext";

export default function OrderSuccess() {
    const [sp] = useSearchParams();
    const sessionId = sp.get("session_id");
    const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
    const { refresh } = useCart();

    useEffect(() => {
        if (!sessionId) {
            setStatus("failed");
            return;
        }
        let attempts = 0;
        const max = 8;
        let timer;
        const poll = async () => {
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    refresh();
                    return;
                }
                if (data.status === "expired") {
                    setStatus("failed");
                    return;
                }
                attempts += 1;
                if (attempts >= max) {
                    setStatus("timeout");
                    return;
                }
                timer = setTimeout(poll, 2000);
            } catch {
                setStatus("failed");
            }
        };
        poll();
        return () => timer && clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    return (
        <div className="grid min-h-[70vh] place-items-center bg-obsidian px-6" data-testid="order-success-page">
            <div className="max-w-xl text-center">
                {status === "checking" && (
                    <>
                        <div className="mx-auto h-16 w-16 animate-pulse rounded-full border border-gold" />
                        <h1 className="mt-8 font-display text-4xl text-white">Holding your hand…</h1>
                        <p className="mt-2 text-white/60">Confirming payment with Stripe.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-gold">
                            <Check className="h-10 w-10 text-gold" />
                        </div>
                        <p className="mt-8 text-[11px] uppercase tracking-[0.4em] text-gold">Thank you</p>
                        <h1 className="mt-3 font-display text-5xl text-white">
                            With love. <Heart className="inline h-8 w-8 text-heart" />
                        </h1>
                        <p className="mt-4 text-white/70">
                            Your order is in. We'll send a confirmation by email and another when it ships.
                        </p>
                        <div className="mt-10 flex flex-wrap justify-center gap-4">
                            <Link
                                to="/account?tab=orders"
                                data-testid="success-view-orders"
                                className="bg-gold px-8 py-3 text-[12px] uppercase tracking-[0.3em] text-obsidian hover:bg-gold-light"
                            >
                                View my orders
                            </Link>
                            <Link
                                to="/shop"
                                className="border border-border-dim px-8 py-3 text-[12px] uppercase tracking-[0.3em] text-white/70 hover:border-gold hover:text-gold"
                            >
                                Keep browsing
                            </Link>
                        </div>
                    </>
                )}
                {status === "timeout" && (
                    <>
                        <h1 className="font-display text-4xl text-white">Almost there.</h1>
                        <p className="mt-2 text-white/60">
                            Stripe is still confirming. You'll receive an email shortly.
                        </p>
                        <Link
                            to="/account?tab=orders"
                            className="mt-8 inline-block bg-gold px-8 py-3 text-[12px] uppercase tracking-[0.3em] text-obsidian"
                        >
                            View orders
                        </Link>
                    </>
                )}
                {status === "failed" && (
                    <>
                        <h1 className="font-display text-4xl text-heart">Something didn't go through.</h1>
                        <p className="mt-2 text-white/60">Don't worry — nothing was charged. Please try again.</p>
                        <Link
                            to="/checkout"
                            className="mt-8 inline-block bg-gold px-8 py-3 text-[12px] uppercase tracking-[0.3em] text-obsidian"
                        >
                            Back to checkout
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
